import {
  TOUR_STEP_IDS,
  type TourStepId,
  SUPERUSER_STEP_IDS,
} from "@/features/intro/lib/tour-steps";
import type { HandlerOutcome } from "@/features/intelligence/lib/frontend-tool-outcome";
import type { IntroRequest } from "@/shared/ui/WorkspaceContext";

export type IntroFocus = "tour" | "capabilities" | TourStepId;

export const INTRO_FOCUSES: readonly IntroFocus[] = ["tour", "capabilities", ...TOUR_STEP_IDS];

export function introRequestFor(focus: IntroFocus): IntroRequest {
  if (focus === "tour") return { from: "simon" };
  if (focus === "capabilities") return { from: "capabilities" };
  return { step: focus };
}

export async function showAppIntro(
  focus: IntroFocus,
  superuser: boolean,
  showIntro: (request: IntroRequest) => Promise<boolean>,
): Promise<HandlerOutcome> {
  if (!superuser && (SUPERUSER_STEP_IDS as readonly string[]).includes(focus)) {
    return {
      ok: false,
      message:
        `Nothing is showing: the tour shows ${PLACES[focus as TourStepId]} only to superusers, ` +
        "and this advisor is not one. Tell the advisor so in one sentence.",
    };
  }
  const shown = await showIntro(introRequestFor(focus));
  if (!shown) return { ok: false, message: notShown(focus) };
  return { ok: true, message: `${shownWhat(focus)} ${SAY_SO}` };
}

function notShown(focus: IntroFocus): string {
  if (focus === "tour" || focus === "capabilities") {
    return (
      "Nothing is showing: the introduction was closed or replaced before it opened. Tell the " +
      "advisor so in one sentence."
    );
  }
  return (
    `Nothing is showing: ${PLACES[focus]} is not on the advisor's screen in this view. Tell ` +
    "the advisor so in one sentence, and do not call this tool again for it."
  );
}

function shownWhat(focus: IntroFocus): string {
  if (focus === "tour") {
    return (
      "The app introduction is now showing over the screen: Simon's presentation first, " +
      "then a tour of the app's parts."
    );
  }
  if (focus === "capabilities") {
    return (
      "The page of what the advisor can ask Simon for is now showing over the screen, with " +
      "an example of each they can try."
    );
  }
  return (
    `A spotlight on ${PLACES[focus]} is now showing, with Simon's card explaining it and a ` +
    "Klart button that closes it."
  );
}

const SAY_SO = "Tell the advisor so in one sentence and nothing more.";

const PLACES: Record<TourStepId, string> = {
  chat: "the message box at the bottom of the start page",
  chips: "the Starta rådgivning and Ny kund shortcuts above the message box",
  cards: "the start page's overview cards",
  clients: "Kunder in the dock",
  docs: "Rådgivningsdokument in the dock",
  portal: "Rådgivningsportal in the dock",
  orders: "Orderhantering in the dock",
  "quant-admin": "Quant-admin in the dock",
  admin: "Administration in the dock",
  fa: "FA in the dock",
  menu: "the side menu",
  rail: "the buttons for earlier conversations, settings and signing out at the bottom left",
};
